import React from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/en'
import 'dayjs/locale/fr'
import 'dayjs/locale/de'
import 'dayjs/locale/es'
import IconButton from '@material-ui/core/IconButton'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import useLanguage from 'shared-module/components/language/useLanguage'
import { getLocaleFormats } from '../utils/dateFormatUtils'

// Enable dayjs plugins
dayjs.extend(localeData)
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)

const useStyles = makeStyles(theme => ({
  datePickerContainer: {
    backgroundColor: theme.palette.background.paper,
    color: '#fff',
    width: 320,
    borderRadius: 5,
    overflow: 'hidden',
    boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  datePickerHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  dateField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#333333',
      borderRadius: '4px',
      color: '#fff',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#555',
      },
      '& fieldset': {
        borderColor: '#444',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#aaa',
    },
  },
  dateFieldSelected: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#333333',
      borderRadius: '4px',
      color: '#fff',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#fff',
      },
      '& fieldset': {
        borderColor: '#939090',
        borderWidth: '1px',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#fff',
    },
  },
  monthSelector: {
    backgroundColor: '#2a2a2a',
    color: '#fff',
    minWidth: 120,
    '& .MuiSelect-icon': {
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  monthHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#2a2a2a',
  },
  navButton: {
    color: '#fff',
    padding: '4px',
  },
  calendarGrid: {
    padding: '8px 16px 16px 16px',
  },
  daysHeader: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    textAlign: 'center',
    marginBottom: '8px',
  },
  dayHeader: {
    fontSize: '12px',
    color: '#888',
    padding: '4px 0',
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
  },
  dayCell: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontSize: '14px',
    cursor: 'pointer',
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&.outside-month': {
      color: '#666',
    },
    '&.selected': {
      backgroundColor: '#FF7A00',
      color: '#fff',
    },
    '&.in-range': {
      backgroundColor: 'rgba(255, 122, 0, 0.5)',
    },
    '&.range-start': {
      backgroundColor: '#DF6E06',
    },
    '&.range-end': {
      backgroundColor: '#DF6E06',
    },
    '&.disabled': {
      pointerEvents: 'none',
      color: '#555',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  infoContainer: {
    padding: '12px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    textAlign: 'center',
  },
  infoText: {
    fontSize: '12px',
    color: '#aaa',
    marginBottom: '4px',
  },
  joinButton: {
    color: '#000',
    backgroundColor: '#FF7A00',
    fontSize: '12px',
    padding: '4px 8px',
    marginLeft: '8px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#E06E00',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  applyButton: {
    color: '#FF7A00',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: '#E06E00',
      color: '#000',
    },
    '&.Mui-disabled': {
      backgroundColor: '#D3CFCF !important',
      color: '#464444 !important',
      opacity: 0.3,
    },
  },
  clearButton: {
    color: '#aaa',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
}))

/**
 * Date picker component for selecting date ranges
 */
const SpyPointDatePicker = ({
  dateRangeState,
  onApply,
  onClear,
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const language = useLanguage()

  const {
    startDate,
    endDate,
    currentMonth,
    selectedMonth,
    dateConstraints,
    userMembership,
    selectedCamera,
    isPrevMonthDisabled,
    isNextMonthDisabled,
    handleDateClick,
    prevMonth,
    nextMonth,
    handleMonthChange,
    getAvailableMonths,
  } = dateRangeState

  // Get locale-specific format patterns
  const formats = getLocaleFormats(language)

  // Set dayjs locale to match user language
  React.useEffect(() => {
    dayjs.locale(language)
  }, [language])

  /**
   * Renders the calendar days
   */
  const renderDays = () => {
    const days = []
    // Use clone to ensure we don't mutate the original
    const firstDay = currentMonth.clone().startOf('month')

    // Calculate the first day to show (might be from previous month)
    const startOfGrid = firstDay.clone().startOf('week')

    // Generate 42 days (6 weeks)
    for (let i = 0; i < 42; i++) {
      // Use add with index to get the correct day without mutating the original object
      const currentDate = startOfGrid.clone().add(i, 'day')
      const isOutsideMonth = currentDate.month() !== currentMonth.month()
      const isSelected = startDate && currentDate.format('YYYY-MM-DD') === startDate.format('YYYY-MM-DD') ||
                          endDate && currentDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD')

      // Check if date is disabled based on constraints
      const isDisabled = currentDate.isBefore(dateConstraints.minDate) ||
                         currentDate.isAfter(dateConstraints.maxDate)

      // Skip rendering days in the future (after today)
      if (currentDate.isAfter(dateConstraints.maxDate)) {
        continue
      }

      let isInRange = false
      let isRangeStart = false
      let isRangeEnd = false

      if (startDate && endDate) {
        isInRange = currentDate.isAfter(startDate) && currentDate.isBefore(endDate)
        isRangeStart = currentDate.format('YYYY-MM-DD') === startDate.format('YYYY-MM-DD')
        isRangeEnd = currentDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD')
      }

      const className = `${classes.dayCell} ${isOutsideMonth ? 'outside-month' : ''} 
                         ${isRangeStart ? 'range-start' : ''} 
                         ${isRangeEnd ? 'range-end' : ''} 
                         ${isInRange ? 'in-range' : ''} 
                         ${isSelected ? 'selected' : ''}
                         ${isDisabled ? 'disabled' : ''}`

      days.push(
        <div
          key={i}
          className={className}
          onClick={() => !isOutsideMonth && !isDisabled && handleDateClick(currentDate)}
          style={isDisabled ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
        >
          { currentDate.date() }
        </div>,
      )
    }

    return days
  }

  return (
    <Box className={classes.datePickerContainer}>
      <Box className={classes.datePickerHeader}>
        <TextField
          label={t('app:filters.dateRange.startDate', { defaultValue: 'Start Date' })}
          variant="outlined"
          className={startDate ? classes.dateFieldSelected : classes.dateField}
          value={startDate
            ? (startDate.isSame(dayjs().startOf('day'), 'day')
                ? t('app:filters.dateRange.today', { defaultValue: 'Today' })
                : startDate.isSame(dayjs().subtract(1, 'day').startOf('day'), 'day')
                  ? t('app:filters.dateRange.yesterday', { defaultValue: 'Yesterday' })
                  : startDate.format(formats.longDate))
            : ''}
          InputProps={{
            readOnly: true,
          }}
          fullWidth
          size="small"
          margin="dense"
        />
        <TextField
          label={t('app:filters.dateRange.endDate', { defaultValue: 'End Date' })}
          variant="outlined"
          className={endDate ? classes.dateFieldSelected : classes.dateField}
          value={endDate
            ? (endDate.isSame(dayjs().startOf('day'), 'day')
                ? t('app:filters.dateRange.today', { defaultValue: 'Today' })
                : endDate.isSame(dayjs().subtract(1, 'day').startOf('day'), 'day')
                  ? t('app:filters.dateRange.yesterday', { defaultValue: 'Yesterday' })
                  : endDate.format(formats.longDate))
            : ''}
          InputProps={{
            readOnly: true,
          }}
          fullWidth
          size="small"
          margin="dense"
        />
      </Box>

      { /* Month Selection */ }
      <Box className={classes.monthHeader}>
        <Select
          value={selectedMonth}
          onChange={handleMonthChange}
          variant="outlined"
          className={classes.monthSelector}
          MenuProps={{
            PaperProps: {
              style: {
                backgroundColor: '#2a2a2a',
                color: '#fff',
              },
            },
          }}
        >
          { getAvailableMonths().map((month) => (
            <MenuItem key={month} value={month} style={{ color: '#fff', backgroundColor: '#2a2a2a' }}>
              { month }
            </MenuItem>
          )) }
        </Select>

        <Box>
          <IconButton
            className={classes.navButton}
            size="small"
            onClick={prevMonth}
            disabled={isPrevMonthDisabled}
            style={{
              opacity: isPrevMonthDisabled ? 0.3 : 1,
            }}
          >
            <KeyboardArrowLeftIcon />
          </IconButton>

          <IconButton
            className={classes.navButton}
            size="small"
            onClick={nextMonth}
            disabled={isNextMonthDisabled}
            style={{
              opacity: isNextMonthDisabled ? 0.3 : 1,
            }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        </Box>
      </Box>

      { /* Calendar */ }
      <Box className={classes.calendarGrid}>
        <Box className={classes.daysHeader}>
          { dayjs.weekdaysMin().map((day, i) => (
            <div key={i} className={classes.dayHeader}>
              { day }
            </div>
          )) }
        </Box>

        <Box className={classes.daysGrid}>
          { renderDays() }
        </Box>
      </Box>

      { /* Info Section */ }
      <Box className={classes.infoContainer}>
        { userMembership && userMembership.isMember
          ? (
            <Typography className={classes.infoText}>
              { t('app:filters.dateRange.historyLimitInsiders', { defaultValue: 'History available: Up to 1 year' }) }
            </Typography>
            )
          : (
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box display="flex" flexDirection="column" alignItems="center" mb={1}>
                <Typography className={classes.infoText}>
                  { t('app:filters.dateRange.historyLimitRegular', { defaultValue: 'History available up to 1 month for Non-Insiders Club Members' }) }
                </Typography>
                <Button
                  size="small"
                  className={classes.joinButton}
                  href="/membership"
                >
                  { t('app:filters.dateRange.joinInsidersClub', { defaultValue: 'Join the Insiders Club' }) }
                </Button>
              </Box>
            </Box>
            ) }
        { selectedCamera && selectedCamera.activationDate && (
          <Typography className={classes.infoText}>
            { t('app:filters.dateRange.cameraActivated', {
              defaultValue: 'Camera activated: {{date}}',
              date: dayjs(selectedCamera.activationDate).format(formats.longDate),
              interpolation: { escapeValue: false },
            }) }
          </Typography>
        ) }
      </Box>

      { /* Action Buttons */ }
      <Box className={classes.buttonContainer}>
        <Button
          onClick={onClear}
          className={classes.clearButton}
        >
          { t('app:filters.dateRange.clear', { defaultValue: 'CLEAR' }) }
        </Button>
        <Button
          onClick={onApply}
          className={classes.applyButton}
          disabled={!startDate || !endDate}
        >
          { t('app:filters.dateRange.apply', { defaultValue: 'APPLY' }) }
        </Button>
      </Box>
    </Box>
  )
}

export default SpyPointDatePicker
